// @flow

import React from 'react';
import { get, partial } from 'lodash';

import { Screen } from '@zapier/theme';
import {
  type ApiKeys,
  type PaymentDetailsBank,
  type PaymentSource,
  type PaymentSourceMethod,
  type SupportedPaymentMethod,
  paymentSourceMethods,
} from '@zapier/payment/src/types';
import { BankAccountForm, PaymentInfo } from '@zapier/payment';
import { Button, Media, Text } from '@zapier/common-components';

import PaymentMethod from '../PaymentMethod';
import PaymentMethodSelect from '../PaymentMethodSelect';

type Props = {
  /** API keys */
  apiKeys: ApiKeys,
  /** Currently select method */
  currentMethod?: PaymentSourceMethod,
  /** Error message to show */
  error?: string,
  /** Hide switch component button */
  hideSwitch: boolean,
  /** Show editor */
  isEditing: boolean,
  /** Show loading */
  isEditingInFlight: boolean,
  /** Call to cancel edit */
  onCancelEdit: () => void,
  /** Call to start edit */
  onEdit: () => void,
  /** Call when editor throws an error */
  onErrorEdit: (error: Error) => void,
  /** Call to save current edit */
  onSaveEdit: () => void,
  /** Call to select a payment source */
  onSelectSource: (method: PaymentSourceMethod) => void,
  /** User's currently saved bank details */
  savedSource: PaymentSource,
  /** Supported payment methods (bank, card etc.) */
  supportedMethods: Array<SupportedPaymentMethod>,
};

const renderLabel = props =>
  props.savedSource && !props.isEditing ? (
    <PaymentInfo paymentSource={props.savedSource} />
  ) : (
    <h3>
      <Text type="section-head">Add Bank Transfer (US only)</Text>
    </h3>
  );

const renderAside = props => (
  <Media queries={{ small: Screen.queries.phoneOnly }}>
    {({ small }) => {
      return props.isEditing ? (
        props.hideSwitch ? null : (
          <PaymentMethodSelect
            currentMethod={props.currentMethod}
            onChange={props.onSelectSource}
            supportedMethods={props.supportedMethods}
          />
        )
      ) : (
        <Button color="secondary" onClick={props.onEdit}>
          {props.savedSource ? `Edit${!small ? ' Payment Method' : ''}` : 'Add'}
        </Button>
      );
    }}
  </Media>
);

const PaymentMethodBank = (props: Props) => {
  const bankDetails: PaymentDetailsBank = get(props, [
    'savedSource',
    'details',
  ]);
  const bankMethod = props.supportedMethods.find(
    m => m.method === paymentSourceMethods.bank
  );

  return (
    <PaymentMethod
      isEditing={props.isEditing}
      renderAside={partial(renderAside, props)}
      renderLabel={partial(renderLabel, props)}
    >
      <BankAccountForm
        braintreeAPIKey={get(props, ['apiKeys', 'braintree'], null)}
        error={props.error}
        isEditingInFlight={props.isEditingInFlight}
        onCancelEdit={props.onCancelEdit}
        onError={props.onErrorEdit}
        onSave={props.onSaveEdit}
        paymentProcessor={get(bankMethod, ['paymentProcessor'])}
        savedAccount={bankDetails}
      />
    </PaymentMethod>
  );
};

export default PaymentMethodBank;
